import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { RevealHeading, StaggerReveal } from "@/components/landing/Motion";
import { ScribbleCircle, Sparkle, Squiggle, BnGlyph } from "@/components/landing/Decorations";
import { Download, Settings, BarChart3, Check, ChevronRight, Plug2, RefreshCw, Globe, Shield } from "lucide-react";

const STEPS = [
    {
        n: "01",
        t: "Download the plugin",
        d: "Click the Download Plugin button on this page to get audo-tts.zip. Save it to your computer.",
        note: null,
    },
    {
        n: "02",
        t: "Upload to WordPress",
        d: "In your WordPress admin, go to Plugins → Add New → Upload Plugin. Select the zip file and click Install Now.",
        note: "WP Admin → Plugins → Add New → Upload Plugin",
    },
    {
        n: "03",
        t: "Activate the plugin",
        d: "Click Activate Plugin after installation. A new Audo TTS item will appear in your WP Admin sidebar.",
        note: null,
    },
    {
        n: "04",
        t: "Get your API key",
        d: "Log into your Audo dashboard. Go to API & Plugins. Create a new key — enter a name and your WordPress site domain. Copy the secret immediately.",
        note: "Dashboard → API & Plugins → Create key",
    },
    {
        n: "05",
        t: "Connect to WordPress",
        d: "In WP Admin → Audo TTS → Settings, paste your API key. Choose your default voice and tone. Hit Save Settings.",
        note: "WP Admin → Audo TTS → Settings",
    },
    {
        n: "06",
        t: "Audio on every post",
        d: "Open any post. Find the Audo TTS meta box (right sidebar). Click Generate Audio. The player appears automatically above your post content — no coding needed.",
        note: "New posts auto-generate on publish",
    },
];

const FEATURES = [
    {icon: Download,   t: "1-click install",     d: "~256KB. Works with any theme since WordPress 5.0."},
    {icon: Settings,   t: "Zero theme changes",   d: "Player injects above post content automatically."},
    {icon: BarChart3,  t: "Built-in analytics",   d: "Track plays and completion rate from your Audo dashboard."},
    {icon: RefreshCw,  t: "Auto-generation",      d: "New posts auto-generate audio on publish — no manual action needed."},
    {icon: Globe,      t: "Any voice, any tone",  d: "Choose from 8 Bengali voices and 10 tone styles per post."},
    {icon: Shield,     t: "Secure API keys",      d: "Keys are domain-locked. Rotate anytime from your dashboard."},
];

export default function WordPressPage() {
    return (
        <div className="paper-grain min-h-screen">
            <Nav/>
            <main className="pt-32 pb-24 relative overflow-hidden">
                <ScribbleCircle color="#3D5BFF" className="absolute top-24 right-[6%] w-28 h-28 opacity-50 animate-float-slow"/>
                <BnGlyph glyph="WP" className="absolute -left-4 top-32 text-[180px] lg:text-[280px] leading-none font-bold opacity-[0.04] select-none" color="#0A0A0A"/>
                <Sparkle color="#FF5B3E" className="absolute top-1/3 right-[14%] w-9 h-9 animate-float-fast"/>

                <div className="mx-auto max-w-[1240px] px-5 sm:px-8 relative">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-5">WordPress plugin</p>
                    <RevealHeading as="h1" className="h-display text-[48px] sm:text-[72px] lg:text-[112px] text-ink max-w-[920px]">
                        WordPress to Bengali audio in <span className="italic-display text-coral">6 steps.</span>
                    </RevealHeading>
                    <p className="mt-6 max-w-[640px] text-[17px] text-ink-muted">
                        The official Audo plugin. Free. Works with any theme since WP 5.0. Auto-generates Bengali audio for every post on publish.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a
                            href="/audo-tts.zip"
                            download="audo-tts.zip"
                            className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-coral text-cream-100 rounded-full px-7 py-4 text-[15px] font-semibold transition-all hover:shadow-coral"
                        >
                            <Download size={16}/> Download Plugin (.zip)
                        </a>
                        <Link to="/dashboard/api" className="inline-flex items-center justify-center gap-2 text-[14px] font-semibold text-ink hover:text-coral underline-offset-4 hover:underline px-2 py-4">
                            Get your API key ▸
                        </Link>
                    </div>

                    {/* Steps */}
                    <StaggerReveal className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-7">
                        {STEPS.map((s, i) => (
                            <div data-stagger key={i} className="bg-white rounded-3xl p-7 lg:p-8 border border-cream-300 shadow-soft">
                                <span className="font-display italic-display text-[80px] leading-none text-coral">{s.n}</span>
                                <h3 className="mt-3 font-display text-[28px] font-semibold tracking-tight text-ink">{s.t}</h3>
                                <p className="mt-3 text-[15px] text-ink-muted leading-relaxed">{s.d}</p>
                                {s.note && (
                                    <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] bg-cream-100 text-ink-muted px-3 py-1.5 rounded-xl font-mono border border-cream-300">
                                        <ChevronRight size={10} className="text-coral shrink-0"/>
                                        {s.note}
                                    </p>
                                )}
                            </div>
                        ))}
                    </StaggerReveal>

                    <Squiggle color="#FFD23F" className="my-20 w-32 mx-auto"/>

                    {/* Features */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div key={i} className="bg-cream-200 rounded-3xl p-6 border border-cream-300">
                                    <div className="w-11 h-11 rounded-2xl bg-coral text-white flex items-center justify-center mb-4">
                                        <Icon size={18}/>
                                    </div>
                                    <h4 className="font-display text-[22px] font-semibold tracking-tight text-ink mb-1">{f.t}</h4>
                                    <p className="text-[14px] text-ink-muted">{f.d}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Compatibility */}
                    <div className="mt-16 bg-ink text-cream-100 rounded-3xl p-10">
                        <h3 className="font-display text-[28px] sm:text-[36px] font-semibold tracking-tight mb-5">Compatibility</h3>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {["WordPress 5.0+", "PHP 7.4+", "Gutenberg + Classic Editor", "Multisite supported", "WPML / Polylang", "AMP-friendly"].map((c, i) => (
                                <li key={i} className="flex items-center gap-2.5 text-[14px]"><Check size={14} className="text-coral"/>{c}</li>
                            ))}
                        </ul>
                    </div>

                    {/* CTA */}
                    <div className="mt-12 text-center">
                        <a
                            href="/audo-tts.zip"
                            download="audo-tts.zip"
                            className="inline-flex items-center gap-2 bg-coral hover:bg-ink text-white rounded-full px-8 py-4 text-[15px] font-semibold transition-all"
                        >
                            <Download size={16}/> Download Plugin — It's Free
                        </a>
                        <p className="mt-3 text-[13px] text-ink-muted">Need an API key? <Link to="/dashboard/api" className="text-coral hover:underline font-semibold">Get one from your dashboard →</Link></p>
                    </div>
                </div>
            </main>
            <Footer/>
        </div>
    );
}
